import { Utils } from '../../common/misc/Utils';
import type InputBaseModel from '../models/inputs/InputBaseModel';
import type ComparisonNodeBaseModel from '../models/contract_comparison/ComparisonNodeBaseModel';
import {
    ContractComparisonSectionsResultDto,
    ContractComparisonSectionMatchDto,
    ContractComparisonSummaryEntry,
    InputChangeRecord,
    SummaryInputChangeRecord,
    SectionPair
} from '../types';

type SectionSummaryMetadata = Pick<ContractComparisonSectionsResultDto, 'context'> & {
    matches: ContractComparisonSectionMatchDto[];
};

export type SummaryPayloadState = {
    requestJson: string;
    signatureHash: number;
};

export class ContractComparisonSummaryPayloadBuilder {
    private readonly signatureParts: string[] = [];

    constructor(private readonly metadata?: SectionSummaryMetadata) {}

    build(nodes: ComparisonNodeBaseModel[]): SummaryPayloadState {
        this.signatureParts.length = 0;

        if (this.metadata) {
            this.signatureParts.push(
                JSON.stringify(this.metadata.context),
                JSON.stringify(
                    [...this.metadata.matches].sort((left, right) =>
                        left.newSectionId.localeCompare(right.newSectionId)
                    )
                )
            );
        }

        const payloadEntries = nodes.flatMap(node =>
            node.panels.map(panel => {
                const groups = node.groups.filter(group => group.panelId === panel.id);

                this.signatureParts.push(panel.name);

                return {
                    entryName: panel.name,
                    changes: this.serializeChanges(
                        groups.filter(group => !group.pair).flatMap(group => group.changes.changeRecords)
                    ),
                    sections: groups.flatMap(group =>
                        group.pair ? [this.serializeSection(group.pair, group.changes.changeRecords)] : []
                    )
                };
            })
        );

        return {
            requestJson: JSON.stringify(payloadEntries),
            signatureHash: Utils.stringToHash(this.signatureParts.join('\u0001'))
        };
    }

    private serializeChanges(records: InputChangeRecord[]): SummaryInputChangeRecord[] {
        return records.map(record => {
            const changeType = record.changeType === 'unsaved' ? 'modified' : record.changeType;

            this.signatureParts.push(
                record.inputId,
                record.inputName,
                changeType,
                JSON.stringify(record.newInput?.dto.value ?? null) ?? 'null',
                JSON.stringify(record.previousInput?.dto.value ?? null) ?? 'null'
            );

            return {
                inputId: record.inputId,
                inputName: record.inputName,
                newValue: this.getSummaryValue(record.newInput),
                previousValue: this.getSummaryValue(record.previousInput),
                changeType
            };
        });
    }

    private serializeSection(pair: SectionPair, records: InputChangeRecord[]): ContractComparisonSummaryEntry {
        if (!this.metadata) {
            throw new Error('Section comparison summary requires confirmed section metadata');
        }

        const section: NonNullable<ContractComparisonSummaryEntry['section']> = {
            newSectionId: pair.newSection?.id,
            newSectionName: pair.newSection?.name,
            previousSectionObjectId: pair.previousSection?.objectId,
            previousSectionName: pair.previousSection?.name,
            status: pair.kind === 'matched' ? undefined : 'unmatched'
        };

        this.signatureParts.push(pair.id, JSON.stringify(section));

        return {
            entryName: pair.kind === 'previousOnly' ? pair.previousSection.name : pair.newSection.name,
            section,
            changes: this.serializeChanges(records),
            sections: []
        };
    }

    private getSummaryValue(input: InputBaseModel | undefined): unknown {
        return input?.dataType === 'DateTime' ? (input.dto.value ?? null) : (input?.formattedValue ?? null);
    }
}
